import { useState, useRef, useEffect } from 'react';
import { Search, Columns3, Rows3 } from 'lucide-react';
import type { Density } from './useDataTablePrefs';

export interface FilterConfig {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}

export interface DataTableToolbarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterConfig[];
  /** All column ids and labels for the toggle */
  allColumns?: { id: string; label: string }[];
  visibleColumns?: string[];
  onToggleColumn?: (id: string) => void;
  density?: Density;
  onDensityChange?: (d: Density) => void;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

const densityLabels: Record<Density, string> = {
  compact: 'Compact',
  normal: 'Normal',
  comfortable: 'Comfortable',
};

export function DataTableToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters,
  allColumns,
  visibleColumns,
  onToggleColumn,
  density,
  onDensityChange,
  leftSlot,
  rightSlot,
}: DataTableToolbarProps) {
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showDensityMenu, setShowDensityMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const densityMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (columnMenuRef.current && !columnMenuRef.current.contains(e.target as Node)) setShowColumnMenu(false);
      if (densityMenuRef.current && !densityMenuRef.current.contains(e.target as Node)) setShowDensityMenu(false);
    }
    if (showColumnMenu || showDensityMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColumnMenu, showDensityMenu]);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {leftSlot}

      {/* Search */}
      {onSearchChange && (
        <div className="relative flex-1 max-w-xs min-w-[160px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
          <input
            type="text"
            value={search ?? ''}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-line rounded-lg
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
          />
        </div>
      )}

      {/* Filter dropdowns */}
      {filters?.map(f => (
        <select
          key={f.id}
          value={f.value}
          onChange={e => f.onChange(e.target.value)}
          className="px-3 py-2 text-sm bg-white border border-line rounded-lg
            focus:outline-none focus:ring-1 focus:ring-coral-500/20 focus:border-coral-300"
        >
          {f.options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ))}

      {/* Column toggle */}
      {allColumns && onToggleColumn && visibleColumns && (
        <div className="relative" ref={columnMenuRef}>
          <button
            onClick={() => setShowColumnMenu(!showColumnMenu)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-line rounded-lg
              hover:bg-surface-muted transition-colors"
          >
            <Columns3 size={14} className="text-muted" />
            <span className="text-text font-medium text-xs">Columns</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-subtle text-muted font-medium">
              {visibleColumns.length}
            </span>
          </button>

          {showColumnMenu && (
            <div className="absolute top-full left-0 mt-1 w-52 rounded-lg shadow-lg z-50 overflow-hidden
              bg-white border border-line">
              <div className="px-3 py-2 bg-surface-muted border-b border-line">
                <div className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                  Show/Hide Columns
                </div>
              </div>
              <div className="py-1 max-h-64 overflow-auto">
                {allColumns.map(col => {
                  const isVisible = visibleColumns.includes(col.id);
                  return (
                    <button
                      key={col.id}
                      onClick={() => onToggleColumn(col.id)}
                      className={`w-full px-3 py-2 flex items-center gap-2.5 hover:bg-surface-muted transition-colors text-left ${
                        isVisible ? 'bg-surface-muted/50' : ''
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          isVisible
                            ? 'bg-coral-500 border-coral-500'
                            : 'border-line-strong'
                        }`}
                      >
                        {isVisible && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-text">{col.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Density selector */}
      {density && onDensityChange && (
        <div className="relative" ref={densityMenuRef}>
          <button
            onClick={() => setShowDensityMenu(!showDensityMenu)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-line rounded-lg
              hover:bg-surface-muted transition-colors"
          >
            <Rows3 size={14} className="text-muted" />
            <span className="text-text font-medium text-xs">{densityLabels[density]}</span>
          </button>

          {showDensityMenu && (
            <div className="absolute top-full left-0 mt-1 w-44 rounded-lg shadow-lg z-50 overflow-hidden
              bg-white border border-line">
              <div className="px-3 py-2 bg-surface-muted border-b border-line">
                <div className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                  Row Density
                </div>
              </div>
              <div className="py-1">
                {(['compact', 'normal', 'comfortable'] as const).map(d => (
                  <button
                    key={d}
                    onClick={() => { onDensityChange(d); setShowDensityMenu(false); }}
                    className={`w-full px-3 py-2 flex items-center justify-between hover:bg-surface-muted transition-colors ${
                      density === d ? 'bg-surface-muted' : ''
                    }`}
                  >
                    <span className="text-sm text-text">{densityLabels[d]}</span>
                    {density === d && (
                      <svg className="w-4 h-4 text-coral-500" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {rightSlot}
    </div>
  );
}
